/**
 * MonographDemoShell — hardened navigation frame for contrib companion demos.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useDocumentKeyDown,
  useEscapeKey,
  useMediaQuery,
  useUrlHashRoute,
} from "../hooks/index.js";
import { cssProperties } from "../styling/index.js";
import { ErrorBoundary } from "./ErrorBoundary.js";
import {
  KeyboardShortcutHints,
  KeyboardShortcutsDialog,
  type KeyboardShortcut,
} from "./KeyboardShortcuts.js";
import {
  adjacentMonographPanelId,
  DEFAULT_NARROW_VIEWPORT_QUERY,
  DEFAULT_SIDEBAR_WIDTH,
  MonographSidebar,
  MonographTopBar,
} from "./monograph-demo-shell/navigation.js";
import { MONOGRAPH_TOP_BAR_HEIGHT } from "./monograph-demo-shell/top-bar.js";
import type { MonographDemoShellProps } from "./monograph-demo-shell/types.js";
import { SkipLink } from "./SkipLink.js";
import { isEditableElementTarget } from "../utils/index.js";

export { adjacentMonographPanelId } from "./monograph-demo-shell/navigation.js";
export { defineMonographNavigation } from "./monograph-demo-shell/navigation-registry.js";
export type {
  MonographDemoNavEntry,
  MonographDemoShellProps,
  MonographDemoTheme,
} from "./monograph-demo-shell/types.js";
export type {
  DefineMonographNavigationOptions,
  MonographNavigationRegistry,
  MonographNavEntryList,
} from "./monograph-demo-shell/navigation-registry.js";

export function MonographDemoShell<PanelId extends string>({
  nav,
  defaultPanelId,
  isPanelId,
  theme,
  title,
  eyebrow,
  tagline,
  companionPath,
  mainRegionId,
  appLabel,
  shortcutsTitle,
  shortcutsDescription,
  recoveryLabel = "Return to first section",
  rootDataName,
  currentScript,
  narrowViewportQuery = DEFAULT_NARROW_VIEWPORT_QUERY,
  sidebarWidth = DEFAULT_SIDEBAR_WIDTH,
  background,
  surfaceTokens,
  panelSuspenseFallback = null,
}: MonographDemoShellProps<PanelId>): React.JSX.Element {
  const navigation = useMemo(() => {
    const ids: PanelId[] = [];
    const byId = new Map<PanelId, (typeof nav)[number]>();
    for (const entry of nav) {
      ids.push(entry.id);
      byId.set(entry.id, entry);
    }
    return { byId, ids };
  }, [nav]);
  const navIds = navigation.ids;
  const navById = navigation.byId;
  const [activeId, setActiveId] = useUrlHashRoute<PanelId>("panel", defaultPanelId, isPanelId);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isOffCanvas = useMediaQuery(narrowViewportQuery);
  const mainRef = useRef<HTMLElement | null>(null);

  const active = navById.get(activeId) ?? nav[0];
  if (active === undefined) {
    throw new Error("MonographDemoShell requires at least one nav entry");
  }
  const ActivePanel = active.component;

  const handleSelect = useCallback(
    (id: PanelId): void => {
      setActiveId(id);
    },
    [setActiveId],
  );
  const closeDrawer = useCallback((): void => {
    setDrawerOpen(false);
  }, []);
  const toggleDrawer = useCallback((): void => {
    setDrawerOpen((previous) => !previous);
  }, []);
  const selectAdjacent = useCallback(
    (delta: -1 | 1): void => {
      setActiveId(adjacentMonographPanelId(navIds, activeId, delta));
    },
    [activeId, navIds, setActiveId],
  );

  useEffect(() => {
    const main = mainRef.current;
    const scrollTo =
      main === null ? undefined : (main as unknown as { readonly scrollTo?: unknown }).scrollTo;
    if (typeof scrollTo === "function") {
      scrollTo.call(main, { top: 0, left: 0, behavior: "auto" });
    }
  }, [activeId]);

  useEscapeKey(closeDrawer, { enabled: isOffCanvas && drawerOpen });

  useDocumentKeyDown(
    (event) => {
      if (
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.repeat ||
        isEditableElementTarget(event.target)
      ) {
        return;
      }
      if (event.key === "[") {
        event.preventDefault();
        selectAdjacent(-1);
        return;
      }
      if (event.key === "]") {
        event.preventDefault();
        selectAdjacent(1);
        return;
      }
      if (isOffCanvas && event.key.toLowerCase() === "m") {
        event.preventDefault();
        toggleDrawer();
      }
    },
    { enabled: !drawerOpen || isOffCanvas },
  );

  const shortcuts = useMemo<readonly KeyboardShortcut[]>(() => {
    const items: KeyboardShortcut[] = [
      { keys: "[", label: "Previous section", group: "Navigation" },
      { keys: "]", label: "Next section", group: "Navigation" },
      { keys: "?", label: "Open shortcuts", group: "Reference" },
    ];
    if (isOffCanvas) {
      items.splice(2, 0, {
        keys: "M",
        label: "Toggle navigation",
        group: "Navigation",
      });
      if (drawerOpen) {
        items.splice(3, 0, {
          keys: "Esc",
          label: "Close navigation",
          group: "Navigation",
        });
      }
    }
    return items;
  }, [drawerOpen, isOffCanvas]);

  return (
    <div
      data-monograph-demo={rootDataName ?? title}
      data-csk-demo-ready="true"
      className="flex min-h-screen"
      style={cssProperties(
        surfaceTokens,
        {
          // Scoped surface variables (if supplied) first, so the explicit
          // background/font/color below always win over any same-named key.
          "--csk-monograph-top-bar-height": isOffCanvas ? MONOGRAPH_TOP_BAR_HEIGHT : "0px",
        },
        {
          background:
            background ?? `radial-gradient(ellipse at top, ${theme.surface} 0%, ${theme.void} 60%)`,
          fontFamily: theme.sans,
          color: theme.text,
        },
      )}
    >
      <SkipLink
        targetId={mainRegionId}
        style={{
          background: theme.surface,
          color: theme.bright,
          borderColor: theme.border,
        }}
      />

      <MonographSidebar
        activeId={activeId}
        nav={nav}
        navIds={navIds}
        onSelect={handleSelect}
        drawerOpen={drawerOpen}
        isOffCanvas={isOffCanvas}
        onCloseDrawer={closeDrawer}
        theme={theme}
        title={title}
        eyebrow={eyebrow}
        tagline={tagline}
        companionPath={companionPath}
        currentScript={currentScript}
        sidebarWidth={sidebarWidth}
      />

      <main
        id={mainRegionId}
        ref={mainRef}
        tabIndex={-1}
        style={{
          flex: 1,
          overflowY: "auto",
          minWidth: 0,
          outline: "none",
        }}
      >
        {isOffCanvas ? (
          <MonographTopBar
            activeLabel={active.label}
            appLabel={appLabel}
            drawerOpen={drawerOpen}
            onToggleDrawer={toggleDrawer}
            theme={theme}
          />
        ) : null}
        <ErrorBoundary
          key={active.id}
          fallbackTitle={`${active.label} failed to render`}
          fallbackDescription="The panel error was contained. Reset to the first section or choose another section from the navigation rail."
          recoveryLabel={recoveryLabel}
          onRecover={() => {
            setActiveId(defaultPanelId);
          }}
        >
          <Suspense fallback={panelSuspenseFallback}>
            <ActivePanel />
          </Suspense>
        </ErrorBoundary>
      </main>

      <div
        className="fixed bottom-4 right-4 z-[70] flex items-center gap-2.5 rounded-lg border px-2 py-1.5 backdrop-blur"
        style={{
          background: theme.surface,
          border: `1px solid ${theme.border}`,
        }}
      >
        {!isOffCanvas ? <KeyboardShortcutHints shortcuts={shortcuts} maxItems={2} /> : null}
        <KeyboardShortcutsDialog
          shortcuts={shortcuts}
          title={shortcutsTitle}
          description={shortcutsDescription}
          triggerLabel={`Show ${eyebrow} keyboard shortcuts`}
        />
      </div>
    </div>
  );
}
